import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TableSkeleton } from '@/components/ui/TableSkeleton';
import { useNavigate } from '@tanstack/react-router';
import toast from 'react-hot-toast';

import { UserFormModal } from '@/pages/users/components/UserFormModal';
import { UsersHeader } from '@/pages/users/components/Header';
import { ConfirmModal } from '@/components/ConfirmModal';
import {
  useCanManageUsers,
  useUsersForManagement,
  useCreateUser,
  useUpdateUser,
} from '@/pages/users/components/useUserManagement';
import type { UserManagement, CreateUserRequest, UpdateUserRequest } from '@/pages/users/api';
import {
  ROLE_LABELS,
  ROLE_COLORS,
  ROLE_PRIORITY,
  APP_ACCESS_LABELS,
} from '@/pages/users/constants';
import { FlagIcon } from '@/components/FlagIcon';
import { getCampaignMarkets } from '@/api/campaigns';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { PAGE_PADDING, tableScrollMaxHeight } from '@/components/layout/layoutConstants';
import { useHeaderHeight } from '@/components/layout/useHeaderHeight';

export const UsersPage = () => {
  const navigate = useNavigate();

  // Measured rather than hardcoded, so the table stays in view when the header
  // grows (wrapped filters, extra actions).
  const { headerRef, headerHeight } = useHeaderHeight();

  // Check permissions
  const { data: canManageUsers, isLoading: checkingPermission } = useCanManageUsers();

  // Fetch users
  const { data: users = [], isLoading: loadingUsers, error: usersError } = useUsersForManagement();

  // Fetch markets dynamically from campaign data
  const { data: markets = [] } = useQuery({
    queryKey: ['campaign-markets'],
    queryFn: getCampaignMarkets,
    staleTime: Infinity,
  });

  // Mutations
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();

  // UI State
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [departmentFilter, setDepartmentFilter] = useState<string[]>([]);
  const [marketFilter, setMarketFilter] = useState<string[]>([]);
  const [marketFilterMode, setMarketFilterMode] = useState<'any' | 'all'>('any');
  const [channelFilter, setChannelFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserManagement | null>(null);
  const [deletingUser, setDeletingUser] = useState<UserManagement | null>(null);
  const [reactivatingUser, setReactivatingUser] = useState<UserManagement | null>(null);

  // Sorting state
  type SortField = 'name' | 'role' | 'department' | 'last_login' | 'is_active';
  const [sortField, setSortField] = useState<SortField>('role');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg
          className="w-4 h-4 text-slate-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-[#0014cc]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-[#0014cc]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Get unique departments from users
  const departments = useMemo(() => {
    const depts = new Set<string>();
    users.forEach((user) => {
      if (user.department) depts.add(user.department);
    });
    return Array.from(depts).sort();
  }, [users]);

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    const filtered = users.filter((user) => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        (user.department?.toLowerCase().includes(searchLower) ?? false);

      // Role filter (empty array = all)
      const matchesRole = roleFilter.length === 0 || roleFilter.includes(user.role);

      // Department filter (empty array = all)
      const matchesDepartment =
        departmentFilter.length === 0 ||
        (user.department && departmentFilter.includes(user.department));

      // Market filter (empty array = all)
      // 'any' mode: user matches if they have ANY of the selected markets
      // 'all' mode: user matches if they have ALL of the selected markets
      let matchesMarket = true;
      if (marketFilter.length > 0) {
        if (marketFilterMode === 'any') {
          matchesMarket = user.market ? user.market.some((m) => marketFilter.includes(m)) : false;
        } else {
          matchesMarket = user.market ? marketFilter.every((m) => user.market!.includes(m)) : false;
        }
      }

      // Channel filter (empty array = all)
      const matchesChannel =
        channelFilter.length === 0 ||
        (user.channel ? user.channel.some((c) => channelFilter.includes(c)) : false);

      // Status filter (empty array = all)
      const matchesStatus =
        statusFilter.length === 0 ||
        (statusFilter.includes('active') && user.is_active) ||
        (statusFilter.includes('inactive') && !user.is_active);

      return (
        matchesSearch &&
        matchesRole &&
        matchesDepartment &&
        matchesMarket &&
        matchesChannel &&
        matchesStatus
      );
    });

    // Sort
    return filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'role':
          comparison = (ROLE_PRIORITY[a.role] ?? 99) - (ROLE_PRIORITY[b.role] ?? 99);
          break;
        case 'department':
          comparison = (a.department || '').localeCompare(b.department || '');
          break;
        case 'last_login': {
          const aDate = a.last_login ? new Date(a.last_login).getTime() : 0;
          const bDate = b.last_login ? new Date(b.last_login).getTime() : 0;
          comparison = aDate - bDate;
          break;
        }
        case 'is_active':
          comparison = (a.is_active ? 1 : 0) - (b.is_active ? 1 : 0);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [
    users,
    searchQuery,
    roleFilter,
    departmentFilter,
    marketFilter,
    marketFilterMode,
    channelFilter,
    statusFilter,
    sortField,
    sortDirection,
  ]);

  // Handle form submission
  const handleFormSubmit = async (data: CreateUserRequest | UpdateUserRequest) => {
    try {
      if (editingUser) {
        await updateUser.mutateAsync({
          userId: editingUser.id,
          data: data as UpdateUserRequest,
        });
        toast.success('User updated successfully');
      } else {
        await createUser.mutateAsync(data as CreateUserRequest);
        toast.success('User created successfully');
      }
      setShowFormModal(false);
      setEditingUser(null);
    } catch (error) {
      const message =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'An error occurred';
      toast.error(message);
    }
  };

  // Handle deactivate
  const handleDeactivate = async () => {
    if (!deletingUser) return;

    try {
      await updateUser.mutateAsync({
        userId: deletingUser.id,
        data: { is_active: false },
      });
      toast.success('User deactivated successfully');
      setDeletingUser(null);
    } catch (error) {
      const message =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to deactivate user';
      toast.error(message);
    }
  };

  // Handle reactivate
  const handleReactivate = async () => {
    if (!reactivatingUser) return;

    try {
      await updateUser.mutateAsync({
        userId: reactivatingUser.id,
        data: { is_active: true },
      });
      toast.success('User reactivated successfully');
      setReactivatingUser(null);
    } catch (error) {
      const message =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to reactivate user';
      toast.error(message);
    }
  };

  // Format date for display
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Loading states
  if (checkingPermission) {
    return (
      <>
        <Breadcrumb />
        <div className="px-6 pb-6 space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <TableSkeleton rows={8} columns={5} />
          </div>
        </div>
      </>
    );
  }

  // Access denied
  if (!canManageUsers) {
    return (
      <>
        <Breadcrumb />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-600 mb-6">You are not authorized to manage users.</p>
            <button
              onClick={() => navigate({ to: '/' })}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (usersError) {
    return (
      <>
        <Breadcrumb />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Failed to Load Users</h2>
            <p className="text-slate-600 mb-6">Please try again later.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div ref={headerRef} className="shrink-0">
        <UsersHeader
          totalUsers={users.length}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          roleFilter={roleFilter}
          onRoleFilterChange={setRoleFilter}
          departmentFilter={departmentFilter}
          onDepartmentFilterChange={setDepartmentFilter}
          departments={departments}
          markets={markets}
          marketFilter={marketFilter}
          onMarketFilterChange={setMarketFilter}
          marketFilterMode={marketFilterMode}
          onMarketFilterModeChange={setMarketFilterMode}
          channelFilter={channelFilter}
          onChannelFilterChange={setChannelFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          onAddUser={() => {
            setEditingUser(null);
            setShowFormModal(true);
          }}
        />
      </div>
      <div className={`space-y-2 ${PAGE_PADDING}`}>
        {/* Users Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loadingUsers ? (
            <TableSkeleton rows={8} columns={5} />
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500">No users found matching your criteria</p>
            </div>
          ) : (
            <div
              className="overflow-auto"
              style={{ maxHeight: tableScrollMaxHeight(headerHeight) }}
            >
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-[1]">
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-1">
                        User
                        <SortIcon field="name" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSort('role')}
                    >
                      <div className="flex items-center gap-1">
                        Role
                        <SortIcon field="role" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSort('department')}
                    >
                      <div className="flex items-center gap-1">
                        Department
                        <SortIcon field="department" />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">
                      Markets
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">
                      Channels
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">
                      Apps
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSort('last_login')}
                    >
                      <div className="flex items-center gap-1">
                        Last Login
                        <SortIcon field="last_login" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSort('is_active')}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        <SortIcon field="is_active" />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider ">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-4">
                        <div>
                          <div className="font-medium text-slate-900">{user.name}</div>
                          <div className="text-sm text-slate-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex px-2.5 py-1 text-sm font-medium rounded-lg ${
                            ROLE_COLORS[user.role] || 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {ROLE_LABELS[user.role] || user.role}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-600">{user.department || '-'}</td>
                      <td className="px-4 py-4">
                        {user.market && user.market.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {user.market.map((m) => (
                              <span
                                key={m}
                                className="px-2.5 py-1 text-sm bg-slate-100 text-slate-700 rounded-lg inline-flex items-center gap-1.5"
                              >
                                <FlagIcon countryCode={m} className="w-4 h-auto rounded-sm" />
                                <span>{m}</span>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {user.channel && user.channel.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {user.channel.map((c) => (
                              <span
                                key={c}
                                className="px-2.5 py-1 text-sm bg-slate-100 text-slate-700 rounded-lg capitalize"
                              >
                                {c}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {user.app_access && user.app_access.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {user.app_access.map((a) => (
                              <span
                                key={a}
                                className="px-2.5 py-1 text-sm bg-slate-100 text-slate-700 rounded-lg whitespace-nowrap"
                              >
                                {APP_ACCESS_LABELS[a] || a}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-600">
                        {formatDate(user.last_login)}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex px-2.5 py-1 text-sm font-medium rounded-lg ${
                            user.is_active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setEditingUser(user);
                              setShowFormModal(true);
                            }}
                            className="p-2 text-slate-400 hover:text-[#0014cc] hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit user"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>
                          {user.is_active ? (
                            <button
                              onClick={() => setDeletingUser(user)}
                              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Deactivate user"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                                />
                              </svg>
                            </button>
                          ) : (
                            <button
                              onClick={() => setReactivatingUser(user)}
                              className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Reactivate user"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Form Modal */}
        <UserFormModal
          isOpen={showFormModal}
          onClose={() => {
            setShowFormModal(false);
            setEditingUser(null);
          }}
          onSubmit={handleFormSubmit}
          user={editingUser}
          isLoading={createUser.isPending || updateUser.isPending}
          markets={markets}
        />

        {/* Deactivate Confirmation Modal */}
        <ConfirmModal
          isOpen={!!deletingUser}
          onClose={() => setDeletingUser(null)}
          onConfirm={handleDeactivate}
          title="Deactivate User"
          message={`Are you sure you want to deactivate ${deletingUser?.name}? They will no longer be able to log in.`}
          confirmText="Deactivate"
          cancelText="Cancel"
          isLoading={updateUser.isPending}
          variant="danger"
        />

        {/* Reactivate Confirmation Modal */}
        <ConfirmModal
          isOpen={!!reactivatingUser}
          onClose={() => setReactivatingUser(null)}
          onConfirm={handleReactivate}
          title="Reactivate User"
          message={`Are you sure you want to reactivate ${reactivatingUser?.name}? They will be able to log in again.`}
          confirmText="Reactivate"
          cancelText="Cancel"
          isLoading={updateUser.isPending}
          variant="success"
        />
      </div>
    </>
  );
};
